/*
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2024 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import { screen } from "@eisinc/XDL/Helpers";
import Invoices3 from "@eisinc/XDL/Icons/Invoices3";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Icon from "@insite/mobius/Icon";
import Typography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { css } from "styled-components";

interface OwnProps {
    steps: string[];
    currentStep: number;
    setCurrentStep: (i: number) => void;
    setIsOpenItemsStep: (value: boolean) => void;
}

type Props = OwnProps;

export interface PaymentCenterStepperStyles {
    wrapper?: InjectableCss;
    wrapperStyle: InjectableCss;
    stepperItemWrapper: InjectableCss;
}

export const styles: PaymentCenterStepperStyles = {
    wrapper: {
        css: css`
            width: 100%;
        `,
    },
    wrapperStyle: {
        css: css`
            background: ${getColor("common.ui3")};
            display: flex;
            padding: 0;
            list-style: none;
            position: relative;
            margin-left: 30px;
            width: calc(100% - 30px);
            ${screen("lte", "sm")`
                display: flex;
                flex-direction: column;
                margin-left: 0;
                width: 100%;
            `}
        `,
    },
    stepperItemWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            text-transform: uppercase;
            text-align: center;
            background: ${getColor("common.ui3")};
            margin: 0 0 0 -30px;
            clip-path: polygon(35px 50%, 0% 0%, calc(100% - 35px) 0%, 100% 50%, calc(100% - 35px) 100%, 0% 100%);
            height: 65px;
            flex-grow: 1;
            div {
                display: flex;
                justify-content: center;
                align-items: center;
                ${screen("lte", "sm")`
                    padding-top: 10px;
                `}
                ${screen("on", "md")`
                    span {display: none;}
                `}
            }
            ${screen("lte", "sm")`
                clip-path: polygon(100% 0, 100% 80%, 50% 100%, 0 80%, 0 0, 50% 20%);
                margin: -10px 0 0 0;
            `}
            &.active {
                background: ${getColor("primary.main")};
                font-weight: bold;
                color: ${getColor("primary.main")};
                position: relative;
                padding-left: 3px;
                padding-right: 1px;
                border: 2px solid ${getColor("primary.main")};
                div {
                    height: 100%;
                    width: 100%;
                    margin: auto;
                    background: ${getColor("common.ui3")};
                    clip-path: polygon(
                        33px 50%,
                        0% 0%,
                        calc(100% - 33px) 0%,
                        100% 50%,
                        calc(100% - 33px) 100%,
                        0% 100%
                    );
                    ${screen("lte", "sm")`
                        clip-path: polygon(100% 0, 100% 80%, 50% 100%, 0 80%, 0 0, 50% 20%);
                  `}
                }
            }

            &.completed {
                background: ${getColor("primary.main")};
                color: #fff;
                font-weight: bold;
            }

            &.clickable {
                cursor: pointer;
                &:hover {
                    box-shadow: inset 0 0 50px -10px rgba(0, 0, 0, 0.3);
                }
            }

            &:first-child {
                ${screen("gt", "sm")`
                    padding-right: 25px;
                `}
                clip-path: polygon(0% 0%, calc(100% - 35px) 0%, 100% 50%, calc(100% - 35px) 100%, 0% 100%);
                max-width: 150px;
                ${screen("lte", "sm")`
                    clip-path: polygon(100% 0, 100% 80%, 50% 100%, 0 80%, 0 0);
                    max-width: initial;
                `}
                svg {
                    margin: auto;
                }
            }
            &:last-child {
                clip-path: polygon(35px 50%, 0% 0%, 100% 0%, 100% 100%, 0% 100%);
                &.active div {
                    clip-path: polygon(33px 50%, 0% 0%, 100% 0%, 100% 100%, 0% 100%);
                }
                ${screen("lte", "sm")`
                    clip-path: polygon(100% 0, 100% 100%, 50% 100%, 0 100%, 0 0, 50% 20%);
                    max-width: initial;
                    &.active div {
                        clip-path: polygon(100% 0, 100% 100%, 50% 100%, 0 100%, 0 0, 50% 20%);
                    }
              `}
            }
        `,
    },
};

const PaymentCenterStepper: React.FC<Props> = ({ steps, currentStep, setCurrentStep, setIsOpenItemsStep }) => {

    const goTo = (i: number) => {
        if (i === 0) {
            setIsOpenItemsStep(true);
            return;
        }
        setCurrentStep(i);
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography as="ul" {...styles.wrapperStyle}>
                {steps &&
                    steps.length > 0 &&
                    steps.map((item: any, index: number) => {
                        return (
                            <Typography
                                as="li"
                                key={`stepper${item + index}`}
                                {...styles.stepperItemWrapper}
                                className={`${currentStep === index && "active"} ${currentStep > index && "completed"
                                    } ${index < currentStep && "clickable"}`}
                                onClick={() => index < currentStep && goTo(index)}
                            >
                                {index === 0 && (
                                    <Icon
                                        src={Invoices3}
                                        css={css`
                                            fill: #fff;
                                            color: #fff;
                                        `}
                                        size={30}
                                        title={item}
                                    />
                                )}

                                <Typography as="div">
                                    {
                                        index !== 0 && 
                                        <>
                                            {index}
                                            <span>{`. ${item}`}</span>
                                        </>
                                    }
                                </Typography>
                            </Typography>
                        );
                    })}
            </Typography>
        </StyledWrapper>
    );
};

export default PaymentCenterStepper;
